import { useCallback, useEffect, useMemo, useState } from 'react';

import { isEqual } from 'lodash';

import { api } from '@api/reduxApi';
import { getTrackProps } from '@components/GQSurveyBuilder/utils';
import { slValueFromArray } from '@components/Public/GQSurvey/skipLogic';
import { track } from '@components/tracking';
import { useToaster } from 'components/stores/toaster';

import {
  createEmptyOutcome,
  EMPTY_RULE,
  getFlattenedRules,
  getGroupedRules,
  Rule,
  RuleLogic as RuleLogicType,
  RuleOutcome as RuleOutcomeType
} from '../utils';

interface ValidationError {
  message: string;
  conflict_type: string;
  conflicting_rules: AnswerTagLogic[];
}

// Constants for rule management
const SINGLE_OUTCOME = 1;
const DUAL_OUTCOME = 2;

// Helper functions for rule operations
const createCleanEmptyRule = (): Rule => ({
  logic: {
    operator: '',
    answer_value: ''
  },
  outcomes: [createEmptyOutcome()]
});

export const isRuleEmpty = (rule: Rule): boolean => {
  const hasValidLogic = rule.logic.operator !== '' || rule.logic.answer_value !== '';
  const hasValidOutcomes = rule.outcomes.some((outcome) => outcome.tag_type !== '' || outcome.tag_value !== '');
  return !hasValidLogic && !hasValidOutcomes;
};

const filterValidRules = (rules: Rule[]): Rule[] => {
  const validRules = rules.filter((rule) => !isRuleEmpty(rule));
  return validRules.length === 0 ? [createCleanEmptyRule()] : validRules;
};

const getConflictingRules = (groupedRules: Rule[], validationError: ValidationError): Set<number> => {
  if (!validationError?.conflicting_rules) return new Set();

  const conflictingRules = new Set<number>();
  const conflictingRulesData = validationError.conflicting_rules;

  groupedRules.forEach((rule, index) => {
    rule.outcomes.forEach((outcome) => {
      const hasMatch = conflictingRulesData.some(
        (conflictRule: AnswerTagLogic) =>
          conflictRule.tag_type === outcome.tag_type &&
          conflictRule.operator === rule.logic.operator &&
          JSON.stringify(conflictRule.answer_value) === JSON.stringify(rule.logic.answer_value)
      );

      if (hasMatch) {
        conflictingRules.add(index);
      }
    });
  });

  return conflictingRules;
};

const updateScreenerWithNewRules = (
  screener: Screener,
  questionId: number,
  newRules: Rule[],
  defaultNextQuestionId: number | null
) => ({
  ...screener,
  fields: screener.fields.map((field) => {
    if (field.id === questionId) {
      return {
        ...field,
        answer_tag_logic: getFlattenedRules(newRules),
        next_screener_field_id: defaultNextQuestionId
      };
    }
    return field;
  })
});

export const convertRulesToSkipLogic = (groupedRules: Rule[]): SkipLogic[] => {
  return groupedRules.reduce<SkipLogic[]>((acc, rule) => {
    if (rule.logic.operator === '') return acc;

    const skipToOutcome = rule.outcomes.find((outcome) => outcome.tag_type === 'skip_to');
    if (!skipToOutcome || !skipToOutcome.tag_value) return acc;

    acc.push({
      to: Number(skipToOutcome.tag_value),
      op: rule.logic.operator as SkipLogicOperator,
      value: Array.isArray(rule.logic.answer_value)
        ? slValueFromArray(rule.logic.answer_value)
        : rule.logic.answer_value
    });

    return acc;
  }, []);
};

export const useQuestionRules = (
  question: ScreenerField,
  screener: Screener,
  onClose: () => void,
  setQuestions: (questions: ScreenerField[]) => void,
  onRulesChange?: (hasChanges: boolean, shouldClose?: boolean) => void
) => {
  const showToast = useToaster();
  const [updateStudyScreener, { isLoading }] = api.useUpdateStudyScreenerMutation();

  const rules = question.answer_tag_logic?.length ? getGroupedRules(question.answer_tag_logic) : [EMPTY_RULE];
  const [groupedRules, setGroupedRules] = useState<Rule[]>(rules);
  const [defaultNextQuestionId, setDefaultNextQuestionId] = useState<number | null>(question.next_screener_field_id);
  const [keyCounter, setKeyCounter] = useState(0);
  const [validationErrors, setValidationErrors] = useState<ValidationError | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const flattenedRules = getFlattenedRules(rules);

  const existingSkipLogicFromRules = useMemo(() => {
    return convertRulesToSkipLogic(groupedRules);
  }, [groupedRules]);

  useEffect(() => {
    const flattenedGroupedRules = getFlattenedRules(groupedRules);
    const areEqual = isEqual(flattenedRules, flattenedGroupedRules);

    const hasAnyFieldChanges = groupedRules.some((rule) => {
      const hasLogicChanges = rule.logic.operator !== '' || rule.logic.answer_value !== '';

      const hasOutcomeChanges = rule.outcomes.some((outcome) => outcome.tag_type !== '' || outcome.tag_value !== '');

      return hasLogicChanges || hasOutcomeChanges;
    });

    const hasChanges = !areEqual || hasAnyFieldChanges;

    if (!isSaving) {
      onRulesChange?.(hasChanges);
    }
  }, [groupedRules, flattenedRules, onRulesChange, isSaving]);

  useEffect(() => {
    setValidationErrors(null);
  }, [groupedRules]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    track('saved_question', getTrackProps(screener));

    const updatedScreener = updateScreenerWithNewRules(screener, question.id, groupedRules, defaultNextQuestionId);

    try {
      const data = await updateStudyScreener(updatedScreener).unwrap();
      setQuestions(data.fields);
      setValidationErrors(null);
      onRulesChange?.(false, true);
      setIsSaving(false);
    } catch (error: unknown) {
      const apiError = error as { data?: { errors?: { 'fields.answer_tag_logic'?: ValidationError[] } } };
      const answerTagLogicErrors = apiError.data?.errors?.['fields.answer_tag_logic'];

      if (answerTagLogicErrors && Array.isArray(answerTagLogicErrors)) {
        setValidationErrors(answerTagLogicErrors[0]);
        setIsSaving(false);
        return;
      }

      const errorMessage = Array.isArray(apiError.data?.errors?.['fields.base'])
        ? apiError.data.errors['fields.base'].join(',')
        : 'Something went wrong. Please try again later.';

      showToast({
        heading: 'Failed to update!',
        text: errorMessage,
        icon: 'error'
      });
      setIsSaving(false);
    }
  }, [
    groupedRules,
    screener,
    question.id,
    updateStudyScreener,
    showToast,
    onClose,
    setQuestions,
    defaultNextQuestionId
  ]);

  const toggleOutcome = useCallback((ruleIndex: number) => {
    setGroupedRules((prevRules) => {
      const updatedRules = prevRules.map((rule) => ({
        logic: { ...rule.logic },
        outcomes: [...rule.outcomes]
      }));
      const currentOutcomes = updatedRules[ruleIndex].outcomes;

      if (currentOutcomes.length === SINGLE_OUTCOME) {
        updatedRules[ruleIndex].outcomes.push(createEmptyOutcome());
      } else if (currentOutcomes.length === DUAL_OUTCOME) {
        updatedRules[ruleIndex].outcomes.splice(1, 1);
      }

      return updatedRules;
    });
  }, []);

  const updateRuleLogic = useCallback(
    (ruleIndex: number, operator: RuleLogicType['operator'], answer_value: RuleLogicType['answer_value']) => {
      setGroupedRules((prevRules) => {
        const updatedRules = prevRules.map((rule) => ({
          logic: { ...rule.logic },
          outcomes: [...rule.outcomes]
        }));
        updatedRules[ruleIndex].logic = { operator, answer_value };
        return updatedRules;
      });
    },
    []
  );

  const updateRuleOutcome = useCallback((ruleIndex: number, outcomeIndex: number, outcome: RuleOutcomeType) => {
    setGroupedRules((prevRules) => {
      const updatedRules = prevRules.map((rule) => ({
        logic: { ...rule.logic },
        outcomes: [...rule.outcomes]
      }));
      updatedRules[ruleIndex].outcomes[outcomeIndex] = outcome;
      return updatedRules;
    });
  }, []);

  const duplicateRule = useCallback((ruleIndex: number) => {
    setGroupedRules((prevRules) => {
      const updatedRules = [...prevRules];
      const ruleToDuplicate = updatedRules[ruleIndex];

      const duplicatedRule: Rule = {
        logic: { ...ruleToDuplicate.logic },
        outcomes: ruleToDuplicate.outcomes.map((outcome) => ({ ...outcome }))
      };

      updatedRules.push(duplicatedRule);
      return updatedRules;
    });
  }, []);

  const removeRule = useCallback((index: number) => {
    setGroupedRules((prevRules) => {
      const updatedRules = [...prevRules];
      updatedRules.splice(index, 1);

      const cleanedRules = filterValidRules(updatedRules);
      return cleanedRules;
    });

    setKeyCounter((prev) => prev + 1);
  }, []);

  const addRule = useCallback(() => {
    setGroupedRules((prevRules) => [...prevRules, EMPTY_RULE]);
  }, []);

  const onRemoveAll = useCallback(() => {
    setGroupedRules([createCleanEmptyRule()]);
    setKeyCounter((prev) => prev + 1);
  }, []);

  const conflictingRuleIndexes = validationErrors ? getConflictingRules(groupedRules, validationErrors) : new Set();

  return {
    groupedRules,
    defaultNextQuestionId,
    setDefaultNextQuestionId,
    isLoading,
    handleSave,
    toggleOutcome,
    updateRuleLogic,
    updateRuleOutcome,
    removeRule,
    addRule,
    duplicateRule,
    onRemoveAll,
    keyCounter,
    validationErrors,
    conflictingRuleIndexes,
    existingSkipLogicFromRules
  };
};
